import { CircularProgress, Grid, Typography } from "@mui/material";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RequestType, geocode, setKey } from "react-geocode";
import { useTranslation } from "react-i18next";
import { convertUtcToTzIsoDayJs } from "../../../../util/timezoneConversion";
import { formatCoordinates, isUsablePosition } from "../../utils/positionUtils";
import FormInput from "../common/FormInput";

const mapContainerStyle = {
  width: "100%",
  height: "320px",
};

interface TabPanel5Props {
  data: any;
}

const TabPanel5: React.FC<TabPanel5Props> = ({ data }) => {
  const { t } = useTranslation(["operation"]);
  const [address, setAddress] = useState<string>("");
  const [addressLoading, setAddressLoading] = useState<boolean>(true);

  // @ts-ignore
  const apiKey = window?.appConfig?.ENV === "PROD"
    ? process.env.REACT_APP_GOOGLE_APY_KEY_PROD
    : process.env.REACT_APP_GOOGLE_APY_KEY_DEV;

  const hasValidPosition: boolean = useMemo(() => {
    const { lastPositionAvailable, lastPositionLatitude, lastPositionLongitude } = data;
    return !!(lastPositionAvailable && isUsablePosition(lastPositionLatitude, lastPositionLongitude));
  }, [data.lastPositionAvailable, data.lastPositionLatitude, data.lastPositionLongitude]);

  // Geocode address
  useEffect(() => {
    if (!hasValidPosition) return;
    let cancelled = false;

    const resolve = async () => {
      try {
        const key = localStorage.getItem("geocode_api_key") || apiKey;
        if (!key) throw new Error("API key non trovata");

        setKey(key);
        const response = await geocode(
          RequestType.LATLNG,
          `${data.lastPositionLatitude},${data.lastPositionLongitude}`
        );

        if (!cancelled) {
          setAddress(
            response.results?.length > 0
              ? response.results[0].formatted_address
              : t("vehicle.tabPanel5.addressNotAvailable") as string
          );
        }
      } catch (error) {
        console.warn("[TabPanel5] Geocode failed:", error);
        if (!cancelled) {
          setAddress(t("vehicle.tabPanel5.addressNotAvailable") as string);
        }
      } finally {
        if (!cancelled) setAddressLoading(false);
      }
    };

    resolve();
    return () => { cancelled = true; };
  }, [data.lastPositionLatitude, data.lastPositionLongitude, hasValidPosition, t, apiKey]);

  // Map marker
  const onMapLoad = useCallback((map: google.maps.Map) => {
    new google.maps.Marker({
      position: { lat: data.lastPositionLatitude, lng: data.lastPositionLongitude },
      map,
      title: "Vehicle Last Position",
    });
  }, [data.lastPositionLatitude, data.lastPositionLongitude]);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: apiKey || "",
  });

  if (!hasValidPosition) {
    return (
      <Grid container justifyContent="center" alignItems="center" style={{ minHeight: "300px" }}>
        <Typography variant="body1" color="textSecondary">
          {t("vehicle.tabPanel5.noDataAvailable")}
        </Typography>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={6}>
        <FormInput
          type="text"
          fullWidth
          disabled
          name="latitude"
          label={t("vehicle.tabPanel5.latitude")}
          value={formatCoordinates(data.lastPositionLatitude)}
          size="small"
        />
        <FormInput
          type="text"
          fullWidth
          disabled
          name="longitude"
          label={t("vehicle.tabPanel5.longitude")}
          value={formatCoordinates(data.lastPositionLongitude)}
          size="small"
        />
        <FormInput
          type="text"
          fullWidth
          disabled
          name="lastPositionAddress"
          label={t("vehicle.tabPanel5.address")}
          value={addressLoading ? "" : address}
          size="small"
          InputProps={{
            endAdornment: addressLoading ? <CircularProgress size={18} /> : undefined,
          }}
        />
        <FormInput
          type="date"
          fullWidth
          disabled
          name="lastPositionTimestamp"
          label={t("vehicle.tabPanel5.lastUpdated")}
          inputFormat="DD-MM-YYYY HH:mm:ss"
          value={data.lastPositionTimestamp ? convertUtcToTzIsoDayJs(data.lastPositionTimestamp, data.timeZoneId) : null}
        />
        {/**
        <FormInput
          type="text"
          fullWidth
          disabled
          name="lastPositionSource"
          label={t("vehicle.tabPanel5.source")}
          value={data.lastPositionSource || "-"}
          size="small"/>
        */}
      </Grid>
      <Grid item xs={6}>
        {isLoaded ? (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={{ lat: data.lastPositionLatitude, lng: data.lastPositionLongitude }}
              zoom={15}
              onLoad={onMapLoad}
              options={{
                mapTypeControl: true,
                streetViewControl: false,
                fullscreenControl: true,
              }}
            />
        ) : (
          <div style={{ ...mapContainerStyle, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f5f5f5" }}>
            <p>{apiKey ? "Loading map..." : "Map unavailable: API key not configured"}</p>
          </div>
        )}
      </Grid>
    </Grid>
  );
};

export default TabPanel5;
